import React, { useMemo, useState } from "react";
import {
  LocationStatus,
  SupplierLocationDisplayVO
} from "../../../../types/LocationStatus";
import { LocationTabConfig } from "./LocationTabConfig";
import LocationTabBar from "./LocationTabBar";
import LocationTabPanel from "./LocationTabPanel";

interface LocationListTabsProps {
  tabs: LocationTabConfig[];
  locations: SupplierLocationDisplayVO[];
  onEdit: (location: SupplierLocationDisplayVO) => void;
  onDeactivateSuccess: (location: SupplierLocationDisplayVO) => void;
  onDeactivateFailure: (errorMessage: string) => void;
  onDeleteSuccess: (location: SupplierLocationDisplayVO) => void;
  onDeleteFailure: (errorMessage: string) => void;
  onSubmitForReviewSuccess: (location: SupplierLocationDisplayVO) => void;
  onSubmitForReviewFailure: (errorMessage: string) => void;
  onCloneSuccess: (location: SupplierLocationDisplayVO) => void;
  onCloneFailure: (errorMessage: string) => void;
  canClone: boolean;
  /** Called after a supplier saves/uploads capacity so the list can refresh. */
  onCapacityUpdated?: () => void;
  isEmailVerified: boolean;
}

/**
 * Owns the active tab state and composes the tab bar with the filtered location panels.
 *
 * Locations are grouped by derivedStatus once per change (single pass per tab) and the
 * resulting buckets feed both the tab counts and each panel — no location array
 * is filtered more than once. All panels stay mounted (hidden via `display:none`)
 * to preserve scroll position.
 *
 * @example
 * <LocationListTabs
 *   tabs={LOCATION_TAB_CONFIGS}
 *   locations={locations}
 *   onEdit={handleEditLocation}
 *   onDeactivateSuccess={handleDeactivateSuccess}
 *   onDeactivateFailure={handleDeactivateFailure}
 *   onDeleteSuccess={handleDeleteSuccess}
 *   onDeleteFailure={handleDeleteFailure}
 *   onSubmitForReviewSuccess={handleSubmitForReviewSuccess}
 *   onSubmitForReviewFailure={handleSubmitForReviewFailure}
 *   isEmailVerified={account?.emailVerified}
 * />
 */
const LocationListTabs = ({
  tabs,
  locations,
  onEdit,
  onDeactivateSuccess,
  onDeactivateFailure,
  onDeleteSuccess,
  onDeleteFailure,
  onSubmitForReviewSuccess,
  onSubmitForReviewFailure,
  onCloneSuccess,
  onCloneFailure,
  canClone,
  onCapacityUpdated,
  isEmailVerified,
}: LocationListTabsProps) => {
  const [activeTab, setActiveTab] = useState<LocationStatus>("active");

  // Group locations by derivedStatus once; reused for both counts and panels.
  const groupedLocations = useMemo(
    () =>
      tabs.reduce<Record<LocationStatus, SupplierLocationDisplayVO[]>>(
        (acc, tab) => {
          acc[tab.variant] = locations.filter((loc: SupplierLocationDisplayVO) => loc.derivedStatus === tab.variant);
          return acc;
        },
        {} as Record<LocationStatus, SupplierLocationDisplayVO[]>
      ),
    [tabs, locations]
  );

  const counts = useMemo(
    () =>
      tabs.reduce<Record<LocationStatus, number>>(
        (acc, tab) => {
          acc[tab.variant] = groupedLocations[tab.variant].length;
          return acc;
        },
        {} as Record<LocationStatus, number>
      ),
    [tabs, groupedLocations]
  );

  return (
    <>
      <LocationTabBar
        tabs={tabs}
        counts={counts}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      {tabs.map((tab) => (
        <LocationTabPanel
          key={tab.variant}
          variant={tab.variant}
          locations={groupedLocations[tab.variant]}
          activeTab={activeTab}
          onEdit={onEdit}
          onDeactivateSuccess={onDeactivateSuccess}
          onDeactivateFailure={onDeactivateFailure}
          onDeleteSuccess={onDeleteSuccess}
          onDeleteFailure={onDeleteFailure}
          onSubmitForReviewSuccess={onSubmitForReviewSuccess}
          onSubmitForReviewFailure={onSubmitForReviewFailure}
          onCloneSuccess={onCloneSuccess}
          onCloneFailure={onCloneFailure}
          canClone={canClone}
          onCapacityUpdated={onCapacityUpdated}
          isEmailVerified={isEmailVerified}
        />
      ))}
    </>
  );
};

export default LocationListTabs;

